import { useState, useEffect } from 'react';
import { FiAlertCircle, FiCheck, FiX, FiCreditCard, FiCalendar, FiPackage, FiHome, FiUsers, FiClock, FiMapPin } from 'react-icons/fi';
import { subscriptionsAPI } from '../../services/api';
import { useApp } from '../../context/AppContext';
import { SUBSCRIPTION_STATUS, SUCCESS_MESSAGES } from '../../utils/constants';

const STATUS_LABELS = {
  [SUBSCRIPTION_STATUS.TRIAL]: 'Período de Prueba',
  [SUBSCRIPTION_STATUS.ACTIVE]: 'Activa',
  [SUBSCRIPTION_STATUS.PAST_DUE]: 'Pago Pendiente',
  [SUBSCRIPTION_STATUS.CANCELED]: 'Cancelada',
  [SUBSCRIPTION_STATUS.EXPIRED]: 'Expirada',
  [SUBSCRIPTION_STATUS.SUSPENDED]: 'Suspendida'
};

const STATUS_COLORS = {
  [SUBSCRIPTION_STATUS.TRIAL]: 'bg-blue-100 text-blue-800',
  [SUBSCRIPTION_STATUS.ACTIVE]: 'bg-green-100 text-green-800',
  [SUBSCRIPTION_STATUS.PAST_DUE]: 'bg-yellow-100 text-yellow-800',
  [SUBSCRIPTION_STATUS.CANCELED]: 'bg-orange-100 text-orange-800',
  [SUBSCRIPTION_STATUS.EXPIRED]: 'bg-red-100 text-red-800',
  [SUBSCRIPTION_STATUS.SUSPENDED]: 'bg-gray-100 text-gray-800'
};

const SubscriptionSettings = () => {
  const { addNotification } = useApp();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchSubscriptionHistory();
    fetchAvailablePlans();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const data = await subscriptionsAPI.getMyStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      showNotification('Error al cargar el estado de la suscripción', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionHistory = async () => {
    try {
      const history = await subscriptionsAPI.getMyHistory();
      setSubscriptionHistory(history);
    } catch (error) {
      console.error('Error al cargar el historial de suscripciones:', error);
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const plans = await subscriptionsAPI.getPlans({ include_inactive: false });
      setAvailablePlans(plans);
    } catch (error) {
      console.error('Error al cargar los planes disponibles:', error);
    }
  };

  const handleSubscribePlan = async (plan, period) => {
    try {
      setLoadingCheckout(true);

      // Crear checkout en PayPal usando el endpoint de renovación
      const response = await subscriptionsAPI.renewCheckout(plan.plan, period);

      // Redirigir a PayPal para el pago
      if (response.approval_url) {
        window.location.href = response.approval_url;
      } else {
        showNotification('Error al crear el proceso de pago', 'error');
      }
    } catch (error) {
      showNotification(
        error.response?.data?.detail || 'Error al iniciar la contratación',
        'error'
      );
    } finally {
      setLoadingCheckout(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      await subscriptionsAPI.cancelSubscription(
        subscriptionStatus.subscription.id,
        {
          immediately: cancelImmediately,
          reason: cancelReason
        }
      );

      showNotification(SUCCESS_MESSAGES.SUBSCRIPTION_CANCELED, 'success');
      setShowCancelModal(false);
      setCancelReason('');
      fetchSubscriptionStatus();
    } catch (error) {
      showNotification(
        error.response?.data?.detail || 'Error al cancelar la suscripción',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type) => {
    addNotification({ message, type });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculatePriceWithIVA = (price, planConfig) => {
    if (!planConfig) return price;

    // Si los precios ya incluyen IVA, devolver el precio tal cual
    if (planConfig.prices_include_iva) {
      return price;
    }

    // Si no incluyen IVA, calcularlo y sumarlo
    const ivaPercentage = planConfig.iva_percentage || 21;
    return price * (1 + ivaPercentage / 100);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  if (loading && !subscriptionStatus) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando información de suscripción...</p>
        </div>
      </div>
    );
  }

  const subscription = subscriptionStatus?.subscription;
  const isExpired = subscriptionStatus?.is_expired;
  const isInTrial = subscriptionStatus?.is_in_trial;
  const daysUntilExpiry = subscriptionStatus?.days_until_expiry;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Mi Suscripción</h1>

        {/* No tiene suscripción */}
        {!subscription && (
          <>
            <div className="bg-white rounded-lg shadow-md p-8 text-center mb-8">
              <FiAlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                No tienes una suscripción activa
              </h2>
              <p className="text-gray-600">
                Selecciona un plan para comenzar a utilizar el servicio
              </p>
            </div>

            {/* Planes disponibles */}
            {availablePlans.length > 0 && (
              <div className="grid md:grid-cols-3 gap-6">
                {availablePlans.map((plan) => {
                  const monthlyPriceWithIVA = calculatePriceWithIVA(plan.monthly_price, plan);
                  const annualPriceWithIVA = calculatePriceWithIVA(plan.annual_price, plan);

                  return (
                    <div
                      key={plan.id}
                      className="bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-blue-500 transition-all p-6"
                    >
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {plan.name}
                      </h3>
                      {plan.description && (
                        <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                      )}

                      {/* Precio mensual */}
                      <div className="mb-4 pb-4 border-b border-gray-200">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-3xl font-bold text-blue-600">
                            {formatPrice(monthlyPriceWithIVA)}
                          </span>
                          <span className="text-gray-600">/mes</span>
                        </div>
                        <div className="text-xs text-gray-500 text-center mt-1">
                          IVA {plan.iva_percentage || 21}% incluido
                        </div>
                        <button
                          onClick={() => handleSubscribePlan(plan, 'monthly')}
                          disabled={loadingCheckout}
                          className="w-full mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {loadingCheckout ? 'Procesando...' : 'Suscripción Mensual'}
                        </button>
                      </div>

                      {/* Precio anual */}
                      <div className="mb-4">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-3xl font-bold text-green-600">
                            {formatPrice(annualPriceWithIVA)}
                          </span>
                          <span className="text-gray-600">/año</span>
                        </div>
                        {plan.annual_discount_percentage && (
                          <div className="text-xs text-green-600 font-semibold text-center mt-1">
                            ¡Ahorra {plan.annual_discount_percentage}%!
                          </div>
                        )}
                        <div className="text-xs text-gray-500 text-center">
                          IVA {plan.iva_percentage || 21}% incluido
                        </div>
                        <button
                          onClick={() => handleSubscribePlan(plan, 'annual')}
                          disabled={loadingCheckout}
                          className="w-full mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {loadingCheckout ? 'Procesando...' : 'Suscripción Anual'}
                        </button>
                      </div>

                      {/* Características */}
                      <div className="space-y-2 text-sm text-gray-700">
                        {plan.max_rooms ? (
                          <div className="flex items-center gap-2">
                            <FiHome className="w-4 h-4 text-blue-600" />
                            <span>Hasta {plan.max_rooms} habitaciones</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <FiHome className="w-4 h-4 text-blue-600" />
                            <span>Habitaciones ilimitadas</span>
                          </div>
                        )}
                        {plan.max_users ? (
                          <div className="flex items-center gap-2">
                            <FiUsers className="w-4 h-4 text-blue-600" />
                            <span>Hasta {plan.max_users} usuarios</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <FiUsers className="w-4 h-4 text-blue-600" />
                            <span>Usuarios ilimitados</span>
                          </div>
                        )}
                        {plan.max_common_spaces ? (
                          <div className="flex items-center gap-2">
                            <FiMapPin className="w-4 h-4 text-green-600" />
                            <span>Hasta {plan.max_common_spaces} espacios comunes</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <FiMapPin className="w-4 h-4 text-green-600" />
                            <span>Espacios comunes ilimitados</span>
                          </div>
                        )}
                        {/* Solo mostrar trial si el usuario nunca ha tenido suscripción */}
                        {plan.trial_days > 0 && subscriptionHistory.length === 0 && (
                          <div className="flex items-center gap-2">
                            <FiCheck className="w-4 h-4 text-green-600" />
                            <span>{plan.trial_days} días de prueba gratis</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Tiene suscripción */}
        {subscription && (
          <>
            {/* Alert si está expirada o por expirar */}
            {isExpired && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-red-900">Suscripción Expirada</h3>
                    <p className="text-red-800 mt-1">
                      Tu suscripción ha expirado. Renueva para continuar utilizando el servicio.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!isExpired && daysUntilExpiry <= 7 && (
              <div className={`border rounded-lg p-4 mb-6 ${
                daysUntilExpiry <= 1
                  ? 'bg-red-50 border-red-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-start gap-3">
                  <FiAlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    daysUntilExpiry <= 1 ? 'text-red-600' : 'text-yellow-600'
                  }`} />
                  <div>
                    <h3 className={`font-semibold ${
                      daysUntilExpiry <= 1 ? 'text-red-900' : 'text-yellow-900'
                    }`}>
                      Tu suscripción {isInTrial ? 'de prueba ' : ''}vence pronto
                    </h3>
                    <p className={daysUntilExpiry <= 1 ? 'text-red-800' : 'text-yellow-800'}>
                      {daysUntilExpiry === 0 && 'Tu suscripción vence hoy. '}
                      {daysUntilExpiry === 1 && 'Tu suscripción vence mañana. '}
                      {daysUntilExpiry > 1 && `Tu suscripción vence en ${daysUntilExpiry} días. `}
                      {subscription.auto_renew
                        ? 'Se renovará automáticamente.'
                        : 'No se renovará automáticamente, deberás renovarla manualmente.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Información principal de la suscripción */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    Plan {subscription.plan_config?.name || subscription.plan}
                  </h2>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    STATUS_COLORS[subscription.status]
                  }`}>
                    {STATUS_LABELS[subscription.status]}
                  </span>
                </div>
                {subscription.plan_config && (
                  <div className="text-right">
                    <div className="space-y-1">
                      {/* Precio base (sin IVA) */}
                      <div className="text-sm text-gray-600">
                        Base: {subscription.period === 'monthly'
                          ? formatPrice(subscription.plan_config.monthly_price)
                          : formatPrice(subscription.plan_config.annual_price)}
                      </div>

                      {/* Precio total (con IVA) */}
                      <div className="flex items-center justify-end gap-2">
                        <div className="text-3xl font-bold text-blue-600">
                          {subscription.period === 'monthly'
                            ? formatPrice(calculatePriceWithIVA(subscription.plan_config.monthly_price, subscription.plan_config))
                            : formatPrice(calculatePriceWithIVA(subscription.plan_config.annual_price, subscription.plan_config))}
                        </div>
                      </div>

                      <div className="text-sm text-gray-600">
                        {subscription.period === 'monthly' ? 'por mes' : 'por año'}
                      </div>

                      <div className="text-xs text-gray-500">
                        (IVA {subscription.plan_config.iva_percentage || 21}% incluido)
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Límites del plan */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FiPackage className="w-4 h-4" />
                    Límites del Plan
                  </h3>
                  <div className="space-y-4">
                    {/* Habitaciones */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 flex items-center gap-1.5">
                          <FiHome className="w-3.5 h-3.5" />
                          Habitaciones
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {subscription.current_usage?.rooms || 0} / {
                            subscription.plan_config?.max_rooms || '∞'
                          }
                        </span>
                      </div>
                      {subscription.plan_config?.max_rooms && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              (subscription.current_usage?.rooms || 0) >= subscription.plan_config.max_rooms
                                ? 'bg-red-500'
                                : (subscription.current_usage?.rooms || 0) / subscription.plan_config.max_rooms >= 0.75
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            style={{
                              width: `${Math.min(
                                ((subscription.current_usage?.rooms || 0) / subscription.plan_config.max_rooms) * 100,
                                100
                              )}%`
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Usuarios */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 flex items-center gap-1.5">
                          <FiUsers className="w-3.5 h-3.5" />
                          Usuarios
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {subscription.current_usage?.users || 0} / {
                            subscription.plan_config?.max_users || '∞'
                          }
                        </span>
                      </div>
                      {subscription.plan_config?.max_users && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              (subscription.current_usage?.users || 0) >= subscription.plan_config.max_users
                                ? 'bg-red-500'
                                : (subscription.current_usage?.users || 0) / subscription.plan_config.max_users >= 0.75
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            style={{
                              width: `${Math.min(
                                ((subscription.current_usage?.users || 0) / subscription.plan_config.max_users) * 100,
                                100
                              )}%`
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Espacios Comunes */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 flex items-center gap-1.5">
                          <FiMapPin className="w-3.5 h-3.5" />
                          Espacios Comunes
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {subscription.current_usage?.common_spaces || 0} / {
                            subscription.plan_config?.max_common_spaces || '∞'
                          }
                        </span>
                      </div>
                      {subscription.plan_config?.max_common_spaces && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              (subscription.current_usage?.common_spaces || 0) >= subscription.plan_config.max_common_spaces
                                ? 'bg-red-500'
                                : (subscription.current_usage?.common_spaces || 0) / subscription.plan_config.max_common_spaces >= 0.75
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            style={{
                              width: `${Math.min(
                                ((subscription.current_usage?.common_spaces || 0) / subscription.plan_config.max_common_spaces) * 100,
                                100
                              )}%`
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fechas */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FiCalendar className="w-4 h-4" />
                    Período de Facturación
                  </h3>
                  <div className="space-y-2 text-sm">
                    {isInTrial ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Inicio de Prueba:</span>
                          <span className="font-medium text-gray-900">
                            {formatDate(subscription.trial_start)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Fin de Prueba:</span>
                          <span className="font-medium text-gray-900">
                            {formatDate(subscription.trial_end)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Inicio:</span>
                          <span className="font-medium text-gray-900">
                            {formatDate(subscription.current_period_start)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Renovación:</span>
                          <span className="font-medium text-gray-900">
                            {formatDate(subscription.current_period_end)}
                          </span>
                        </div>
                      </>
                    )}
                    {daysUntilExpiry !== null && daysUntilExpiry < 30 && (
                      <div className="flex items-center gap-1 pt-2 border-t border-gray-100">
                        <FiClock className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-xs font-medium text-blue-600">
                          {daysUntilExpiry} {daysUntilExpiry === 1 ? 'día restante' : 'días restantes'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Renovación automática */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FiCreditCard className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">Renovación Automática</span>
                  </div>
                  <span className={`flex items-center gap-2 ${
                    subscription.auto_renew ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {subscription.auto_renew ? (
                      <>
                        <FiCheck className="w-5 h-5" />
                        Activada
                      </>
                    ) : (
                      <>
                        <FiX className="w-5 h-5" />
                        Desactivada
                      </>
                    )}
                  </span>
                </div>
                {subscription.auto_renew && !isInTrial && !isExpired && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-900">
                      <strong>Próxima renovación:</strong> {formatDate(subscription.current_period_end)} a las 00:00
                    </p>
                    <p className="text-sm text-blue-800 mt-1">
                      Tu suscripción se renovará automáticamente y se procesará el pago correspondiente.
                    </p>
                  </div>
                )}
                {subscription.auto_renew && isInTrial && !isExpired && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-900">
                      <strong>Inicio de facturación:</strong> {formatDate(subscription.trial_end)} a las 00:00
                    </p>
                    <p className="text-sm text-blue-800 mt-1">
                      Al finalizar tu período de prueba, se iniciará la facturación automática mensual.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Acciones */}
            {!isExpired && subscription.status !== SUBSCRIPTION_STATUS.CANCELED && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Gestionar Suscripción
                </h3>

                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={loading}
                  className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  Cancelar Suscripción
                </button>
              </div>
            )}

            {/* Suscripción cancelada pero aún activa */}
            {subscription.status === SUBSCRIPTION_STATUS.CANCELED && !isExpired && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Suscripción Cancelada
                </h3>
                <p className="text-gray-600 mb-4">
                  Tu suscripción está cancelada pero seguirás teniendo acceso hasta el{' '}
                  <strong>{formatDate(subscription.current_period_end)}</strong>.
                </p>
              </div>
            )}

            {/* Historial de Suscripciones */}
            {subscriptionHistory.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FiCalendar className="w-5 h-5" />
                  Historial de Suscripciones
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Plan
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Período
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Inicio
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fin
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {subscriptionHistory.map((sub) => (
                        <tr key={sub.id} className={sub.id === subscription?.id ? 'bg-blue-50' : ''}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {sub.plan}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_COLORS[sub.status]}`}>
                              {STATUS_LABELS[sub.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {sub.period === 'monthly' ? 'Mensual' : 'Anual'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {sub.status === 'trial' && sub.trial_start
                              ? formatDate(sub.trial_start)
                              : formatDate(sub.current_period_start)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {sub.status === 'trial' && sub.trial_end
                              ? formatDate(sub.trial_end)
                              : formatDate(sub.current_period_end)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de cancelación */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Cancelar Suscripción
            </h2>

            <p className="text-gray-600 mb-4">
              ¿Estás seguro de que deseas cancelar tu suscripción?
            </p>

            <div className="mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cancelImmediately}
                  onChange={(e) => setCancelImmediately(e.target.checked)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">Cancelar inmediatamente</div>
                  <div className="text-sm text-gray-600">
                    {cancelImmediately
                      ? 'Perderás el acceso ahora mismo'
                      : 'Mantendrás el acceso hasta el final del período pagado'}
                  </div>
                </div>
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo de cancelación (opcional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ayúdanos a mejorar contándonos por qué cancelas..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelImmediately(false);
                  setCancelReason('');
                }}
                disabled={loading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                No, mantener
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Cancelando...' : 'Sí, cancelar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSettings;
